import { Calendar } from "@tamagui/lucide-icons";
import dayjs from "dayjs";
import type { Timestamp } from "firebase/firestore";
import { observer } from "mobx-react-lite";
import { useState } from "react";
import { Switch, styled, ToggleGroup, View, XStack, YStack } from "tamagui";
import { DatePicker } from "../../../core/components/FormDataModal";
import { getDayJsDateAndTimeWithFormat } from "../../../core/dayJs";
import { Body } from "../../../ui/Body";
import { Input } from "../../../ui/InputNew";

export const BusinessDatePickerForm: React.FC<{
  value?: Date | string; // YYYY-MM-DD
  onChange?: (value: Date) => void;
}> = (props) => {
  const [sheetOpen, setSheetOpen] = useState(false);
  const [date, setDate] = useState(
    props.value ? dayjs(props.value).format("YYYY-MM-DD") : "",
  );

  let formattedDate = date;

  if (date) {
    const isValidDate = dayjs(date, "YYYY-MM-DD", true).isValid();

    if (isValidDate) {
      formattedDate = getDayJsDateAndTimeWithFormat(
        new Date(date),
        "YYYY-MM-DD",
      );
    }
  }

  return (
    <>
      <XStack>
        <Input
          type="border"
          placeholder="YYYY-MM-DD"
          value={formattedDate}
          onChangeText={(text) => {
            setDate(text);

            if (text && dayjs(text, "YYYY-MM-DD", true).isValid()) {
              props.onChange?.(new Date(text));
            }
          }}
        />
        <View
          backgroundColor={"$backgroundMutedBlock"}
          borderColor="$divider"
          borderWidth={1}
          borderLeftWidth={0}
          alignItems="center"
          justifyContent="center"
          cursor="pointer"
          width={50}
          onPress={() => setSheetOpen(true)}
        >
          <Calendar />
        </View>
      </XStack>
      <DatePicker
        calendarProps={{
          minDate: dayjs(new Date()).format("YYYY-MM-DD"),
        }}
        sheetId="date_picker"
        value={formattedDate}
        open={sheetOpen}
        onOpenChange={(open: boolean) => setSheetOpen(open)}
        onSelect={(newDate) => {
          props.onChange?.(new Date(newDate));
          setDate(dayjs(newDate).format("YYYY-MM-DD"));
          setSheetOpen(false);
        }}
      />
    </>
  );
};

const ToggleGroupItem = styled(ToggleGroup.Item, {
  color: "$color",
  backgroundColor: "transparent",
  borderRadius: 0,
  paddingHorizontal: 23,
  paddingVertical: 14,

  hoverStyle: {
    backgroundColor: "$mutedBackground",
    color: "$color",
  },

  focusStyle: {
    backgroundColor: "$pressablePrimaryBackground",
    color: "$pressablePrimaryLabel",
  },

  variants: {
    active: {
      true: {
        hoverStyle: {
          backgroundColor: "$pressablePrimaryBackground",
          /// @ts-expect-error tamagui typing issue, it's working...
          color: "$pressablePrimaryLabel",
        },

        backgroundColor: "$pressablePrimaryBackground",
        color: "$pressablePrimaryLabel",
      },
    },
  },
});

const OffsetDateText: React.FC<{
  startingDate?: Timestamp | null;
  offsetDays?: number;
}> = observer(({ startingDate, offsetDays }) => {
  if (startingDate && offsetDays) {
    const date = startingDate.toDate();
    date.setDate(date.getDate() + offsetDays);

    const direction =
      offsetDays === 0
        ? "the same day as"
        : Math.abs(offsetDays) === 1
          ? offsetDays > 0
            ? "1 day after"
            : "1 day before"
          : `${Math.abs(offsetDays)} days ${offsetDays > 0 ? "after" : "before"}`;

    return (
      <small>
        The date will be <strong>{dayjs(date).format("YYYY-MM-DD")}</strong>.{" "}
        {direction} the events starting date{" "}
        {dayjs(startingDate.toDate()).format("YYYY-MM-DD")}.
      </small>
    );
  }

  if (startingDate) {
    return (
      <small>
        No offset selected. The date will be the same as the event's starting{" "}
        {dayjs(startingDate.toDate()).format("YYYY-MM-DD")}.
      </small>
    );
  }

  if (offsetDays !== undefined) {
    const direction =
      offsetDays === 0
        ? "the same day as"
        : Math.abs(offsetDays) === 1
          ? offsetDays > 0
            ? "1 day after"
            : "1 day before"
          : `${Math.abs(offsetDays)} days ${offsetDays > 0 ? "after" : "before"}`;

    return (
      <small>The date will be {direction} the event's starting date.</small>
    );
  }
});

export const BusinessOffsetDateSelector: React.FC<{
  offsetDays?: number;
  date?: Date;
  onChange?: (props: { date: Date } | { offset: number }) => void;
  startingDate?: Timestamp | null;
}> = observer((props) => {
  const [type, setType] = useState<"offset" | "date">(
    props.date ? "date" : "offset",
  );
  const [offset, setOffset] = useState<number | undefined>(props.offsetDays);
  const [date, setDate] = useState<Date | undefined>(props.date);

  return (
    <YStack gap="$medium">
      <ToggleGroup
        borderRadius={0}
        backgroundColor={"transparent"}
        id={"dateToggle"}
        type={"single"} // since this demo switches between loosen types
        disableDeactivation
        value={type}
        onValueChange={(value: "offset" | "date") => {
          if (value === "offset" && offset !== undefined) {
            props.onChange?.({ offset });
          } else if (value === "date" && date) {
            props.onChange?.({ date });
          }

          setType(value);
        }}
      >
        <ToggleGroupItem active={type === "offset"} value="offset">
          <Body>Offset</Body>
        </ToggleGroupItem>
        <ToggleGroupItem active={type === "date"} value="date">
          <Body>Fixed date</Body>
        </ToggleGroupItem>
      </ToggleGroup>

      {type === "offset" && (
        <YStack gap="$small">
          <XStack gap="$large">
            <Input
              type="border"
              value={offset ? String(offset) : ""}
              placeholder="Offset"
              onChangeText={(value) => {
                const numberValue = Number(value);
                if (!Number.isNaN(numberValue)) {
                  setOffset(numberValue);
                  props.onChange?.({ offset: numberValue });
                }
              }}
            />
            <XStack gap="$small" alignItems="center">
              <Body>before</Body>
              <Switch
                padding={1}
                borderWidth={1}
                borderColor={"$divider"}
                backgroundColor={"$backgroundMutedBlock"}
                size={"$medium"}
                checked={offset !== undefined ? offset > 0 : false}
                id={"offsetBeforeAfter"}
                onCheckedChange={() => {
                  if (offset) {
                    const newOffset = offset * -1;
                    setOffset(newOffset);
                    props.onChange?.({ offset: newOffset });
                  }
                }}
              >
                <Switch.Thumb
                  size={"$medium"}
                  backgroundColor={"$pressablePrimaryBackground"}
                  animation="quicker"
                />
              </Switch>
              <Body>after</Body>
            </XStack>
          </XStack>
          <OffsetDateText
            startingDate={props.startingDate}
            offsetDays={offset}
          />
        </YStack>
      )}
      {type === "date" && (
        <BusinessDatePickerForm
          value={date}
          onChange={(date) => {
            setDate(date);
            props.onChange?.({ date });
          }}
        />
      )}
    </YStack>
  );
});
